import { useState, useEffect, useCallback } from 'react';
import { 
  X, Search, Filter, ChevronDown, Users, CheckCircle, Clock, 
  Send, FileText, XCircle, Eye, Check, Ban, RefreshCw,
  Mail, Phone, Building, Calendar, AlertTriangle, ArrowRight,
  MoreHorizontal, UserPlus, CreditCard
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Metric types configuration
const METRIC_CONFIG = {
  total_carriers: {
    title: 'Total Carriers',
    endpoint: '/hive/admin/metrics/carriers',
    dataKey: 'carriers',
    icon: Users,
    color: '#3B82F6',
    filters: ['tier'],
    actions: ['view'],
  },
  active_carriers: {
    title: 'Active Carriers',
    endpoint: '/hive/admin/metrics/active-carriers',
    dataKey: 'active_carriers',
    icon: CheckCircle,
    color: '#10B981',
    filters: [],
    actions: ['view'],
  },
  trial_users: {
    title: 'Trial Users',
    endpoint: '/hive/admin/metrics/trial-users',
    dataKey: 'trial_users',
    icon: Clock,
    color: '#F59E0B',
    filters: [],
    actions: ['convert', 'extend', 'cancel'],
  },
  pending_onboarding: {
    title: 'Pending Onboard',
    endpoint: '/hive/admin/metrics/pending-onboard',
    dataKey: 'pending_onboard',
    icon: Send,
    color: '#8B5CF6',
    filters: [],
    actions: ['resend_invite'],
  },
  pending_applications: {
    title: 'Applications',
    endpoint: '/hive/admin/metrics/applications',
    dataKey: 'applications',
    icon: FileText,
    color: '#EC4899',
    filters: ['status'],
    actions: ['approve', 'reject'],
  },
  payment_failures: {
    title: 'Payment Issues',
    endpoint: '/hive/admin/metrics/payment-issues',
    dataKey: 'payment_issues',
    icon: XCircle,
    color: '#EF4444',
    filters: [],
    actions: ['resolve'],
  },
};

// Interactive Stat Card
export const InteractiveStatCard = ({ label, value, icon: Icon, color, metricKey, onClick }) => (
  <button
    onClick={() => onClick(metricKey)}
    className="bg-[#121212] border border-white/5 p-3 sm:p-4 text-left w-full hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5 transition-all duration-200 group active:scale-[0.98]"
    data-testid={`stat-card-${metricKey}`}
  >
    <div className="flex items-center justify-between mb-1 sm:mb-2">
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 transition-colors group-hover:text-[#D4AF37] flex-shrink-0" style={{ color }} />
        <span className="text-white/50 text-[10px] sm:text-xs uppercase tracking-wider truncate">{label}</span>
      </div>
      <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/20 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" />
    </div>
    <p className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#D4AF37] transition-colors">{value}</p>
  </button>
);

// Main Detail Panel Component
const AdminDetailPanel = ({ metricKey, onClose, onViewCarrier, onRefreshDashboard }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  const config = METRIC_CONFIG[metricKey];
  const Icon = config?.icon || Users;

  const fetchData = useCallback(async () => {
    if (!config) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('hive_token');
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      
      const res = await axios.get(`${API}${config.endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let items = res.data[config.dataKey] || [];
      
      // Sort data
      if (sortBy === 'newest') {
        items.sort((a, b) => new Date(b.created_at || b.trial_start_date || 0) - new Date(a.created_at || a.trial_start_date || 0));
      } else if (sortBy === 'oldest') {
        items.sort((a, b) => new Date(a.created_at || a.trial_start_date || 0) - new Date(b.created_at || b.trial_start_date || 0));
      } else if (sortBy === 'name') {
        items.sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
      }
      
      setData(items);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [config, searchQuery, statusFilter, tierFilter, sortBy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Action handlers
  const handleAction = async (action, itemId) => {
    setActionLoading(itemId);
    const token = localStorage.getItem('hive_token');
    
    try {
      let endpoint = '';
      let payload = {};
      
      switch (action) {
        case 'approve':
          endpoint = `/hive/admin/applications/${itemId}/action`;
          payload = { action: 'approve', dispatch_tier: 'bronze' };
          break;
        case 'reject':
          endpoint = `/hive/admin/applications/${itemId}/action`;
          payload = { action: 'reject', notes: 'Rejected by admin' };
          break;
        case 'convert':
          endpoint = `/hive/admin/trial/${itemId}/action`;
          payload = { action: 'convert' };
          break;
        case 'extend':
          endpoint = `/hive/admin/trial/${itemId}/action`;
          payload = { action: 'extend' };
          break;
        case 'cancel':
          if (!window.confirm('Are you sure you want to cancel this trial?')) {
            setActionLoading(null);
            return;
          }
          endpoint = `/hive/admin/trial/${itemId}/action`;
          payload = { action: 'cancel' };
          break;
        case 'resolve':
          endpoint = `/hive/admin/payment-issue/${itemId}/resolve`;
          payload = {};
          break;
        case 'resend_invite':
          endpoint = `/hive/admin/onboard/${itemId}/resend-invite`;
          payload = {};
          break;
        default:
          return;
      }
      
      await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data
      fetchData();
      onRefreshDashboard?.();
    } catch (err) {
      console.error('Action error:', err);
      alert('Action failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  if (!config) return null;

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="admin-detail-panel">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="relative w-full max-w-3xl bg-[#0A0A0A] border-l border-white/10 h-full overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 flex items-center justify-center rounded"
              style={{ backgroundColor: `${config.color}20` }}
            >
              <Icon className="w-5 h-5" style={{ color: config.color }} />
            </div>
            <div>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white uppercase tracking-wide">
                {config.title}
              </h2>
              <p className="text-white/50 text-sm">{data.length} total</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-white/10 bg-[#0D0D0D] space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              placeholder="Search by name, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#121212] border border-white/10 text-white pl-10 pr-4 py-2 focus:border-[#D4AF37]/50 focus:outline-none"
            />
          </div>
          
          {/* Filter Row */}
          <div className="flex items-center gap-3">
            {/* Status Filter (for applications) */}
            {config.filters.includes('status') && (
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="bg-[#121212] border border-white/10 text-white px-3 py-2 pr-8 appearance-none cursor-pointer text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
              </div>
            )}
            
            {/* Tier Filter */}
            {config.filters.includes('tier') && (
              <div className="relative">
                <select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="bg-[#121212] border border-white/10 text-white px-3 py-2 pr-8 appearance-none cursor-pointer text-sm"
                >
                  <option value="all">All Tiers</option>
                  <option value="bronze">Bronze</option>
                  <option value="silver">Silver</option>
                  <option value="gold">Gold</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
              </div>
            )}
            
            {/* Sort */}
            <div className="relative ml-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[#121212] border border-white/10 text-white px-3 py-2 pr-8 appearance-none cursor-pointer text-sm"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">By Name</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
            </div>
            
            {/* Refresh */}
            <button
              onClick={fetchData}
              className="p-2 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="w-6 h-6 text-[#D4AF37] animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-400">{error}</p>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12">
              <Icon className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/40">No items found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.map((item) => (
                <ItemCard 
                  key={item.id || item.user_id}
                  item={item}
                  metricKey={metricKey}
                  config={config}
                  onAction={handleAction}
                  actionLoading={actionLoading}
                  onViewCarrier={onViewCarrier}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Item Card Component
const ItemCard = ({ item, metricKey, config, onAction, actionLoading, onViewCarrier }) => {
  const [showActions, setShowActions] = useState(false);
  const isLoading = actionLoading === (item.id || item.user_id);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'trial': return 'bg-amber-500/20 text-amber-400';
      case 'pending': return 'bg-blue-500/20 text-blue-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      case 'suspended': return 'bg-red-500/20 text-red-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'gold': return 'bg-[#D4AF37]/20 text-[#D4AF37]';
      case 'silver': return 'bg-[#C0C0C0]/20 text-[#C0C0C0]';
      case 'bronze': return 'bg-[#CD7F32]/20 text-[#CD7F32]';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '-';
    }
  };

  return (
    <div className="bg-[#121212] border border-white/5 p-4 hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-white truncate">{item.company_name || 'Unknown'}</h3>
            {item.dispatch_tier && (
              <span className={`px-2 py-0.5 text-xs font-medium uppercase ${getTierColor(item.dispatch_tier)}`}>
                {item.dispatch_tier}
              </span>
            )}
          </div>
          
          <p className="text-white/60 text-sm truncate">{item.contact_name}</p>
          
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className="flex items-center gap-1 text-white/40">
              <Mail className="w-3.5 h-3.5" />
              {item.email}
            </span>
            {item.phone && (
              <span className="flex items-center gap-1 text-white/40">
                <Phone className="w-3.5 h-3.5" />
                {item.phone}
              </span>
            )}
            {item.dot_mc_number && (
              <span className="flex items-center gap-1 text-white/40">
                <Building className="w-3.5 h-3.5" />
                MC# {item.dot_mc_number}
              </span>
            )}
          </div>
          
          {/* Status & Date Row */}
          <div className="flex items-center gap-3 mt-3">
            {(item.status || item.subscription_status) && (
              <span className={`px-2 py-0.5 text-xs font-medium ${getStatusColor(item.status || item.subscription_status)}`}>
                {item.status || item.subscription_status}
              </span>
            )}
            
            {item.days_remaining !== undefined && (
              <span className={`px-2 py-0.5 text-xs font-medium ${item.days_remaining <= 3 ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {item.days_remaining} days left
              </span>
            )}
            
            {item.compliance_health_score !== undefined && (
              <span className={`px-2 py-0.5 text-xs font-medium ${item.compliance_health_score >= 75 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {item.compliance_health_score}% health
              </span>
            )}
            
            <span className="flex items-center gap-1 text-white/30 text-xs ml-auto">
              <Calendar className="w-3 h-3" />
              {formatDate(item.created_at || item.trial_start_date || item.approved_at)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-white/40 hover:text-white hover:bg-white/10 transition-colors"
            disabled={isLoading}
          >
            {isLoading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <MoreHorizontal className="w-5 h-5" />
            )}
          </button>
          
          {showActions && !isLoading && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 bg-[#1a1a1a] border border-white/10 shadow-xl z-20 min-w-[160px]">
                {/* View action (always available for carriers) */}
                {item.application_id && onViewCarrier && (
                  <button
                    onClick={() => {
                      onViewCarrier(item.application_id);
                      setShowActions(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-white/70 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] flex items-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View as Carrier
                  </button>
                )}
                
                {/* Approve/Reject for applications */}
                {config.actions.includes('approve') && item.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        onAction('approve', item.id);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-green-500/10 flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        onAction('reject', item.id);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                    >
                      <Ban className="w-4 h-4" />
                      Reject
                    </button>
                  </>
                )}
                
                {/* Trial actions */}
                {config.actions.includes('convert') && (
                  <>
                    <button
                      onClick={() => {
                        onAction('convert', item.user_id);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-green-500/10 flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Convert to Active
                    </button>
                    <button
                      onClick={() => {
                        onAction('extend', item.user_id);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2"
                    >
                      <Clock className="w-4 h-4" />
                      Extend Trial (+7d)
                    </button>
                    <button
                      onClick={() => {
                        onAction('cancel', item.user_id);
                        setShowActions(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel Trial
                    </button>
                  </>
                )}
                
                {/* Payment issue actions */}
                {config.actions.includes('resolve') && (
                  <button
                    onClick={() => {
                      onAction('resolve', item.user_id);
                      setShowActions(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-green-400 hover:bg-green-500/10 flex items-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    Mark Resolved
                  </button>
                )}
                
                {/* Resend invite */}
                {config.actions.includes('resend_invite') && (
                  <button
                    onClick={() => {
                      onAction('resend_invite', item.id);
                      setShowActions(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Resend Invite
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDetailPanel;
